import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import MinSidaShortcuts, { FarmorQuickAsk } from './MinSidaShortcuts';

const MONTH_LABELS = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];

const CONTENT_LINKS = [
  { slug: 'vision-{Y}-{M}', label: 'Månatliga Visionboards', emoji: '🎯', per_month: true },
  { slug: 'playlist-{Y}-{M}', label: 'Månatliga Spellistor', emoji: '🎧', per_month: true },
  { slug: 'books',   label: 'Böcker att läsa',    emoji: '📖' },
  { slug: 'movies',  label: 'Filmer att se',      emoji: '🎬' },
  { slug: 'tv',      label: 'TV att titta på',    emoji: '📺' },
  { slug: 'notes',   label: 'Anteckningar',       emoji: '📝' },
  { slug: 'extras',  label: 'Extras',             emoji: '💗' },
];

export default function MinSidaIndexView({ onSwitchView, onPickDay, onPickListPage, MonthRail }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const now = new Date();
  const curMonth = now.getMonth() + 1;

  const openContent = (link) => {
    if (link.per_month) {
      const key = link.slug
        .replace('{Y}', String(year))
        .replace('{M}', String(curMonth).padStart(2, '0'));
      onPickListPage(key, `${link.emoji} ${link.label} — ${MONTH_LABELS[curMonth - 1]}`);
    } else {
      onPickListPage(link.slug, `${link.emoji} ${link.label}`);
    }
  };

  return (
    <div className="max-w-2xl mx-auto md:pr-12" data-testid="mysida-index">
      {/* Top icon nav bar */}
      <div className="flex items-center justify-between gap-1 mb-5 pb-3 border-b-2 border-[#C8B6FF]/40">
        <button onClick={() => onSwitchView('day')} className="w-11 h-11 flex items-center justify-center hover:scale-110 transition text-3xl text-[#B5A0D0]" title="Idag" data-testid="index-icon-home">🏠</button>
        <button onClick={() => openContent(CONTENT_LINKS[0])} className="w-11 h-11 flex items-center justify-center hover:scale-110 transition text-3xl text-[#B5A0D0]" title="Vision board" data-testid="index-icon-vision">🎯</button>
        <button onClick={() => openContent(CONTENT_LINKS[1])} className="w-11 h-11 flex items-center justify-center hover:scale-110 transition text-3xl text-[#B5A0D0]" title="Playlist" data-testid="index-icon-playlist">🎧</button>
        <button onClick={() => openContent(CONTENT_LINKS[3])} className="w-11 h-11 flex items-center justify-center hover:scale-110 transition text-3xl text-[#B5A0D0]" title="Filmer" data-testid="index-icon-movies">🎬</button>
        <button onClick={() => openContent(CONTENT_LINKS[2])} className="w-11 h-11 flex items-center justify-center hover:scale-110 transition text-3xl text-[#B5A0D0]" title="Böcker" data-testid="index-icon-books">📖</button>
        <button onClick={() => openContent(CONTENT_LINKS[4])} className="w-11 h-11 flex items-center justify-center hover:scale-110 transition text-3xl text-[#B5A0D0]" title="TV" data-testid="index-icon-tv">📺</button>
        <button onClick={() => openContent(CONTENT_LINKS[5])} className="w-11 h-11 flex items-center justify-center hover:scale-110 transition text-3xl text-[#B5A0D0]" title="Anteckningar" data-testid="index-icon-notes">📝</button>
        <button onClick={() => openContent(CONTENT_LINKS[6])} className="w-11 h-11 flex items-center justify-center hover:scale-110 transition text-3xl text-[#B5A0D0]" title="Extras" data-testid="index-icon-extras">💗</button>
      </div>

      {/* Kawaii shortcuts (Farmor · Fasta · Hudvård · Lek-labbet) */}
      <MinSidaShortcuts />

      {/* Farmor Elsa — snabbfråga inbäddad */}
      <FarmorQuickAsk />

      {/* Year picker */}
      <div className="flex items-center justify-center gap-3 mb-5">
        <button onClick={() => setYear(year - 1)} className="neo-btn p-1.5 bg-white" data-testid="index-prev-year"><ChevronLeft className="w-3.5 h-3.5" /></button>
        <div className="font-['Fredoka'] text-2xl font-bold tracking-wider text-[#7D6BA0]">{year}</div>
        <button onClick={() => setYear(year + 1)} className="neo-btn p-1.5 bg-white" data-testid="index-next-year"><ChevronRight className="w-3.5 h-3.5" /></button>
      </div>

      {/* INDEX heading */}
      <div className="mb-2">
        <div className="text-2xl sm:text-3xl font-black tracking-[0.15em] text-[#7D6BA0]">INDEX</div>
        <div className="h-2.5 mt-1 rounded-full bg-[#DED0F0]" />
      </div>

      {/* Planner section */}
      <div className="py-4 space-y-2">
        <button onClick={() => { setYear(year); onSwitchView('year'); }} className="w-full text-left block hover:translate-x-1 transition font-black tracking-widest text-lg sm:text-xl text-[#8B7BAA] py-1" data-testid="index-link-yearly">
          YEARLY PLANNER
        </button>
        <button onClick={() => onSwitchView('month')} className="w-full text-left block hover:translate-x-1 transition font-black tracking-widest text-lg sm:text-xl text-[#8B7BAA] py-1" data-testid="index-link-monthly">
          MONTHLY PLANNERS
        </button>
        <button onClick={() => onSwitchView('week')} className="w-full text-left block hover:translate-x-1 transition font-black tracking-widest text-lg sm:text-xl text-[#8B7BAA] py-1" data-testid="index-link-weekly">
          WEEKLY PLANNERS
        </button>
        <button onClick={() => onSwitchView('day')} className="w-full text-left block hover:translate-x-1 transition font-black tracking-widest text-lg sm:text-xl text-[#8B7BAA] py-1" data-testid="index-link-daily">
          DAILY PLANNERS
        </button>
      </div>

      {/* Divider */}
      <div className="h-2.5 rounded-full bg-[#DED0F0] my-2" />

      {/* Content links */}
      <div className="pt-4 space-y-2">
        {CONTENT_LINKS.map((link, i) => (
          <button
            key={link.slug}
            onClick={() => openContent(link)}
            className="w-full text-left block hover:translate-x-1 transition font-black tracking-widest text-base sm:text-lg text-[#8B7BAA] py-1"
            data-testid={`index-link-content-${i}`}
          >
            {link.label.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Right-side month rail */}
      {MonthRail && (
        <MonthRail
          year={year}
          activeMonth={year === now.getFullYear() ? curMonth : null}
          onPickMonth={(y, m) => { onSwitchView('month', `${y}-${String(m).padStart(2, '0')}-01`); }}
        />
      )}
    </div>
  );
}

export { MONTH_LABELS, CONTENT_LINKS };
